// SidebarShell.tsx
import { type ReactNode, useEffect, useState, useCallback } from 'react';
import { Box, Tabs, Tab } from '@mui/material';

export interface PanelConfig {
    id: string;
    label: string;
    icon: ReactNode;
    disabled?: boolean;
    content: ReactNode;
}

interface SidebarShellProps {
    panels: PanelConfig[];
    activePanel: string | null;
    onPanelChange: (id: string | null) => void;
}

export function SidebarShell({ panels, activePanel, onPanelChange }: SidebarShellProps) {
    // Auto-correct: if active panel is disabled, fall back to first enabled or null
    useEffect(() => {
        if (activePanel && panels.find(p => p.id === activePanel)?.disabled) {
            const firstEnabled = panels.find(p => !p.disabled)?.id ?? null;
            onPanelChange(firstEnabled);
        }
    }, [activePanel, panels, onPanelChange]);

    const activeConfig = activePanel ? panels.find(p => p.id === activePanel && !p.disabled) : undefined;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Tab bar */}
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                }}
            >
                <Tabs
                    value={activePanel ?? false}
                    onChange={(_, value) => onPanelChange(value)}
                    variant="fullWidth"
                    sx={{
                        minHeight: 36,
                        '& .MuiTab-root': {
                            minHeight: 36,
                            py: 0,
                            px: 1,
                            fontSize: '11px',
                            fontWeight: 600,
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                        },
                        '& .MuiTabs-indicator': {
                            height: 2,
                        },
                        '& .Mui-disabled': {
                            opacity: 0.35,
                        },
                    }}
                >
                    {panels.map(panel => (
                        <Tab
                            key={panel.id}
                            value={panel.id}
                            disabled={panel.disabled}
                            icon={panel.icon as React.ReactElement}
                            iconPosition="start"
                            label={panel.label}
                            onClick={() => {
                                if (panel.id === activePanel) onPanelChange(null);
                            }}
                            sx={{
                                gap: 0.5,
                                '& .MuiTab-iconWrapper': {
                                    fontSize: 14,
                                    mr: 0,
                                },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* Active panel content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {activeConfig?.content}
            </Box>
        </Box>
    );
}

// --- Persisted sidebar panel state ---

const STORAGE_PREFIX = 'irm.sidebar.';

export function useSidebarPanel(key: string, fallback: string): [string | null, (id: string | null) => void] {
    const storageKey = STORAGE_PREFIX + key;

    const [panel, setPanel] = useState<string | null>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            return stored ?? fallback;
        } catch {
            return fallback;
        }
    });

    const update = useCallback((id: string | null) => {
        setPanel(id);
        try {
            if (id) localStorage.setItem(storageKey, id);
            else localStorage.removeItem(storageKey);
        } catch { /* quota / private mode */ }
    }, [storageKey]);

    return [panel, update];
}
